import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { useReducedMotion } from 'framer-motion';
import { FREEZE_FOR_CAPTURE } from '@components/motion/scroll-motion';
import { NO_HOW_LIGHT, useWaterStory } from '@components/water/water-story';
import { colors } from '@config/colors';
import { borderRadius, spacing } from '@config/spacing';
import { typography } from '@config/typography';
import { FamilyGroup } from './figures';
import {
  GROUND,
  H,
  Learn,
  LoopScene,
  Night,
  PHONE_BOX,
  Phone,
  Speech,
  W,
  levelTop,
  levelX,
  phoneLeft,
  type Layout,
  type Side,
} from './scenes';
import { CUES, SCENES, TOTAL, clamp, draw, ease, tween } from './timeline';

/*
 * How we build, told as one wish (the design lab's "Learning from Every
 * Wish", 2026-09-22). Maya asks for help getting the kids out of the
 * house and gets today's generic answer — the park, again. That night The
 * Home CEO reviews her wish, and three engines attach, each read large
 * before it settles into place: pre-fetched data, domain expert models,
 * assistant capabilities. Next Saturday Jess makes the same wish and gets
 * the NASA center, on her calendar in one tap. Then the same wish at three
 * levels, side by side, with the families under each. Its three acts —
 * First Mom’s Wish, Behind the Scenes, Next Mom’s Wish — stand across the
 * top, the one playing lit, each a way to its start.
 *
 * The scenes are drawn on a fixed 1600 × 900 stage; a camera frames it.
 * Three across it is the design's own — the whole stage, breathing, with
 * a push in on the phone deep enough for its words to read. On a phone the
 * stage is framed a piece at a time — the family, the phone, the line she
 * says, each level in turn — so every word is read at a size a phone can
 * show.
 *
 * It plays on the page's own water rather than in a box: at its night the
 * whole page's lights go down, the next morning comes up as the WEE
 * story's sunrise, and the three levels stand in its bright day
 * (`useHowLight`, via the shared WaterStory).
 */

const { Arrive: A0, Ask: A, Park: P, Night: N, Learn: B, Return: R, Loop: L } = CUES;

/* The figures are drawn facing left, so each mom walks the way she faces
   (June, 2026-09-23): Maya in from the right and on out to the left, her
   phone and her words on her left; Jess mirrored, in from the left and out
   to the right, at an unhurried walk. */
const MAYA_X = W - 520;
const JESS_X = 520;

/** Jess's family steps and bounces this much slower than drawn. */
const JESS_TEMPO = 0.7;

/** When Jess's phone answers with the NASA center — the page's sunrise
 *  comes up with it (June, 2026-09-23). */
const RECOMMENDED = R + 8.2;

/** Where each mom stands: walking in, and later on her way. Each stays a
 *  while after she speaks, her words up until she's walked off (June,
 *  2026-09-23: time to read them). */
const firstMomX = (T: number) =>
  tween(T, W + 340, MAYA_X, A0, A0 + 3.2, ease.outQuad) - tween(T, 0, 1500, P + 3.8, P + 6.0, ease.inQuad);
const nextMomX = (T: number) =>
  tween(T, -340, JESS_X, R + 0.2, R + 4.4, ease.outQuad) + tween(T, 0, 1400, R + 14.4, R + 18.0, ease.inQuad);

/** How far the lights are down: the night and the backstage. */
const backstage = (T: number) => draw(T, N + 0.4, B - N - 0.8) - draw(T, R - 0.5, 1);

/** How dark the page's night gets: a little of the water still shows. */
const NIGHT_DEPTH = 0.9;

/** The next morning is softer than the WEE story's sunrise, the one burst. */
const DAWN = 0.7;

/** The page's light at T: the night the assistant learns in, the sunrise
 *  that comes up as Jess's answer arrives (and how far its sun has risen),
 *  the day of the three levels. */
const howLight = (T: number) => ({
  night: backstage(T),
  dawn: DAWN * draw(T, RECOMMENDED - 0.2, 2.4, ease.outCubic) * (1 - draw(T, L - 2.5, 2.5)),
  rise: draw(T, RECOMMENDED - 0.2, 3.2, ease.outCubic),
  /* Down again as the levels fade, so the telling loops back into Maya's
     ordinary morning. */
  day: draw(T, L + 0.3, 1.6, ease.outCubic) * (1 - draw(T, TOTAL - 1.6, 1.4)),
});

/** On a phone the families under each level stand closer to their cards. */
const loopGround = (layout: Layout) => (layout === 'tall' ? 690 : GROUND);

/** A colour that follows the story's night toward another: the story sets
 *  --how-night (0 day … 1 night) on the section's column, for the heading
 *  and the acts across the top. */
export const byNight = (day: string, night: string) =>
  `color-mix(in srgb, ${day}, ${night} calc(var(--how-night, 0) * 100%))`;

/** The telling's three acts (June, 2026-09-23): three rectangles across
 *  the top, each a button to its act's start. The one playing fills with
 *  coral as it plays; the others stay slate. The three levels end the
 *  last. */
const ACTS = [
  { key: 'first', at: A0, until: N, label: 'First Mom’s Wish', short: 'First Mom' },
  { key: 'behind', at: N, until: R, label: 'Behind the Scenes', short: 'Behind the Scenes' },
  { key: 'next', at: R, until: TOTAL, label: 'Next Mom’s Wish', short: 'Next Mom' },
] as const;

type ActsProps = {
  T: number;
  onSeek: (t: number) => void;
  width?: number;
  /** Over the stage's top edge rather than above it (see ACTS_OVER). */
  over: boolean;
};

const Acts: React.FC<ActsProps> = ({ T, onSeek, width, over }) => {
  const current = ACTS.findIndex((act) => T >= act.at && T < act.until);
  return (
    // Steering the story, not taking over the page: the page keeps playing
    // (see useAutoplay).
    <ActRow role="group" aria-label="The story’s three acts" style={{ width }} $over={over} data-autoplay-steer>
      <ActGrid>
        {ACTS.map((act, i) => {
          const progress = clamp((T - act.at) / (act.until - act.at), 0, 1);
          return (
            <ActButton
              key={act.key}
              type="button"
              aria-label={act.label}
              aria-current={i === current ? 'step' : undefined}
              onClick={() => onSeek(act.at)}
            >
              {i === current && <ActFill style={{ transform: `scaleX(${progress})` }} />}
              <ActName aria-hidden="true">
                <Long>{act.label}</Long>
                <Short>{act.short}</Short>
              </ActName>
              {/* The name again in slate, shown only where the coral has
                  filled: white on the slate, slate on the coral, turning at
                  the fill's edge. */}
              {i === current && (
                <ActNameOnFill aria-hidden="true" style={{ clipPath: `inset(0 ${(1 - progress) * 100}% 0 0)` }}>
                  <ActName>
                    <Long>{act.label}</Long>
                    <Short>{act.short}</Short>
                  </ActName>
                </ActNameOnFill>
              )}
            </ActButton>
          );
        })}
      </ActGrid>
    </ActRow>
  );
};

const Piece: React.FC<{ T: number; layout: Layout }> = ({ T, layout }) => {
  const x1 = firstMomX(T);
  const x2 = nextMomX(T);
  return (
    <>
      {T < N && (
        <>
          <FamilyGroup
            T={T}
            x={x1}
            ground={GROUND}
            family={1}
            kids={2}
            size={240}
            energy={1 - 0.45 * draw(T, A0 + 2.8, 0.8) - 0.3 * draw(T, P + 0.5, 0.7) + 0.15 * draw(T, P + 3.6, 0.5)}
            mood={0.65 - 0.5 * draw(T, P + 0.5, 0.7)}
            holding={T >= A0 + 3.6 && T < P}
          />
          <Phone T={T} at={A0 + 4.0} until={A + 6.9} typeAt={A + 0.2} typeDur={1.8} replyAt={A + 3.5} version={1} side="left" />
          <Speech T={T} at={P + 0.1} until={P + 5.2} x={x1} side="left" layout={layout}>
            Hokay kids… we’re going to the park. For the umpteenth time.
          </Speech>
        </>
      )}
      {T >= N && T < B && <Night T={T} start={N} endAt={B} layout={layout} />}
      {T >= B && T < R && <Learn T={T} start={B} endAt={R} layout={layout} />}
      {T >= R && T < L && (
        <>
          <FamilyGroup
            T={T}
            x={x2}
            ground={GROUND}
            family={2}
            kids={2}
            size={240}
            facing="right"
            tempo={JESS_TEMPO}
            energy={1 - 0.4 * draw(T, R + 3.8, 0.8) + 0.9 * draw(T, R + 12.0, 0.6)}
            mood={0.65 + 0.55 * draw(T, R + 11.8, 0.5)}
            holding={T >= R + 4.6 && T < R + 11.6}
          />
          <Phone T={T} at={R + 5.0} until={R + 11.6} typeAt={R + 5.8} typeDur={1.6} replyAt={RECOMMENDED} version={2} tapAt={R + 10.4} side="right" />
          <Speech T={T} at={R + 11.8} until={R + 16.6} x={x2} side="right" layout={layout}>
            Kids — the new Ames NASA center just opened. We’re going!
          </Speech>
        </>
      )}
      {T >= L && <LoopScene T={T} start={L} endAt={TOTAL} ground={loopGround(layout)} top={levelTop(layout)} />}
    </>
  );
};

// ── The camera ───────────────────────────────────────────────────────────────

/** What the frame shows: a centre on the stage and how wide a slice. */
type Camera = { cx: number; cy: number; wv: number };

const FULL: Camera = { cx: W / 2, cy: H / 2, wv: W };

const sceneAt = (T: number) => {
  let index = 0;
  SCENES.forEach((scene, i) => {
    if (T >= CUES[scene.name]) index = i;
  });
  return index;
};

/** How far the wide camera pushes in on the phone: enough for the chat to
 *  read at ~12.5px at this width, never less than the design's 1.08, and
 *  never so far that the family's feet leave the frame. */
const focusZoom = (frameWidth: number) => clamp(12.5 / ((18 * frameWidth) / W), 1.08, 1.25);

/** The design's camera: each scene breathes a slow 2% push in or out, and
 *  the phone scenes lean in on the phone — to the left for Maya's, to the
 *  right for Jess's. */
function wideCamera(T: number, zoom: number): Camera {
  const index = sceneAt(T);
  const from = CUES[SCENES[index].name];
  const to = index + 1 < SCENES.length ? CUES[SCENES[index + 1].name] : TOTAL;
  const frac = clamp((T - from) / (to - from), 0, 1);
  const breath = index % 2 === 0 ? frac : 1 - frac;
  const mayaPhone = draw(T, A0 + 4.0, 0.8) * (1 - draw(T, A + 6.2, 0.7));
  const jessPhone = draw(T, R + 5.0, 0.8) * (1 - draw(T, R + 11.0, 0.7));
  const focus = mayaPhone + jessPhone;
  const scale = 1 + 0.02 * breath + (zoom - 1) * focus;
  const ox = 800 + 200 * (jessPhone - mayaPhone);
  /* A little high of centre: the phone's top stays clear of the acts over
     the frame's top edge, and the family's feet stay in. */
  const oy = 450 - 30 * focus;
  return { wv: W / scale, cx: ox + (W / 2 - ox) / scale, cy: oy + (H / 2 - oy) / scale };
}

/** A phone's frame: height over width. */
const TALL = 5 / 4;

/** The smallest phone-shaped frame around a box on the stage. */
const box = (x0: number, x1: number, y0: number, y1: number): Camera => ({
  cx: (x0 + x1) / 2,
  cy: (y0 + y1) / 2,
  wv: Math.max(x1 - x0, (y1 - y0) / TALL),
});

/** Keep a phone frame on the stage. */
const onStage = (c: Camera): Camera => {
  const hv = c.wv * TALL;
  return { wv: c.wv, cx: clamp(c.cx, c.wv / 2, W - c.wv / 2), cy: clamp(c.cy, hv / 2, H - hv / 2) };
};

const SHOTS = {
  family: (x: number) => box(x - 270, x + 270, 400, 880),
  phone: (side: Side) =>
    box(
      phoneLeft(side) - 20,
      phoneLeft(side) + PHONE_BOX.width + 20,
      PHONE_BOX.top - 20,
      PHONE_BOX.top + PHONE_BOX.height + 20,
    ),
  /* The mom, and her words on the side she faces. */
  speech: (x: number, side: Side) => (side === 'right' ? box(x - 230, x + 470, 300, 880) : box(x - 470, x + 230, 300, 880)),
  night: box(500, 1100, 60, 800),
  learn: box(500, 1100, 70, 800),
  level: (i: number) => box(levelX(i) - 240, levelX(i) + 240, levelTop('tall') - 20, loopGround('tall') + 30),
};

const MOVE = 0.8;

/** On a phone: each shot is where the camera has arrived by its time; it
 *  moves there over the MOVE seconds before. */
function tallCamera(T: number): Camera {
  const x1 = firstMomX(T);
  const x2 = nextMomX(T);
  const keys: ReadonlyArray<readonly [number, Camera]> = [
    [0, SHOTS.family(x1)],
    [A0 + 4.6, SHOTS.phone('left')],
    // Her words stay up as she walks off: the camera goes with her.
    [P, SHOTS.speech(x1, 'left')],
    [N + 0.2, SHOTS.night],
    [B + 0.5, SHOTS.learn],
    [R + 0.6, SHOTS.family(x2)],
    [R + 5.6, SHOTS.phone('right')],
    [R + 12.2, SHOTS.speech(x2, 'right')],
    [L + 0.8, SHOTS.level(0)],
    [L + 4.0, SHOTS.level(1)],
    [L + 7.0, SHOTS.level(2)],
  ];
  let k = 0;
  while (k + 1 < keys.length && T >= keys[k + 1][0] - MOVE) k++;
  const here = onStage(keys[k][1]);
  if (k === 0) return here;
  const before = onStage(keys[k - 1][1]);
  const p = draw(T, keys[k][0] - MOVE, MOVE);
  return {
    cx: before.cx + (here.cx - before.cx) * p,
    cy: before.cy + (here.cy - before.cy) * p,
    wv: before.wv + (here.wv - before.wv) * p,
  };
}

// ── The clock ────────────────────────────────────────────────────────────────

/** The frame a still telling shows: the three levels side by side, the
 *  story in one picture (on a phone, the whole day planned). */
const STILL_T = L + 6;

/** Seconds into the telling, and a way to move it (the acts). It starts
 *  from the top each time the story comes into view, runs only while it's
 *  in view, and loops; each time a telling finishes it says so — the
 *  page's autoplay moves on then. Still, it holds the three levels. */
function useStoryClock(
  ref: React.RefObject<HTMLElement>,
  still: boolean,
  onCycleEnd?: () => void,
): [number, (t: number) => void] {
  const [T, setT] = useState(still ? STILL_T : 0);
  const [inView, setInView] = useState(false);
  const onEnd = useRef(onCycleEnd);
  const time = useRef(still ? STILL_T : 0);

  useEffect(() => {
    onEnd.current = onCycleEnd;
  }, [onCycleEnd]);

  useEffect(() => {
    const el = ref.current;
    if (!el || still) return undefined;
    const observer = new IntersectionObserver(([entry]) => setInView(entry.isIntersecting), { threshold: 0.35 });
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref, still]);

  useEffect(() => {
    if (still) {
      time.current = STILL_T;
      setT(STILL_T);
      return undefined;
    }
    if (!inView) {
      time.current = 0;
      setT(0);
      return undefined;
    }
    let frame = 0;
    let last = 0;
    const tick = (now: number) => {
      /* A hidden tab stops the frames; don't let the story jump ahead. */
      if (last) time.current += Math.min(0.1, (now - last) / 1000);
      last = now;
      if (time.current >= TOTAL) {
        time.current -= TOTAL;
        onEnd.current?.();
      }
      setT(time.current);
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [inView, still]);

  const seek = (t: number) => {
    time.current = t;
    setT(t);
  };
  return [T, seek];
}

/* Three across on a landscape screen from 600px, and on any screen from
   1200px; phones and portrait tablets take the phone-shaped frame, where
   the stage's words read at a size the width allows. */
const WIDE_QUERY = '(min-width: 600px) and (orientation: landscape), (min-width: 1200px)';

function useMatches(query: string): boolean {
  const [matches, setMatches] = useState(() => typeof window !== 'undefined' && window.matchMedia(query).matches);
  useEffect(() => {
    const list = window.matchMedia(query);
    const onChange = () => setMatches(list.matches);
    onChange();
    list.addEventListener('change', onChange);
    return () => list.removeEventListener('change', onChange);
  }, [query]);
  return matches;
}

function useSize(ref: React.RefObject<HTMLElement>) {
  const [size, setSize] = useState({ w: 0, h: 0 });
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const measure = () => setSize({ w: el.clientWidth, h: el.clientHeight });
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref]);
  return size;
}

/** The screen's height without a phone browser's collapsible bars, so the
 *  stage doesn't resize as they come and go. */
function steadyScreenHeight(): number {
  const probe = document.createElement('div');
  probe.style.cssText = 'position:fixed;top:0;left:0;width:0;height:100vh;height:100svh;visibility:hidden;pointer-events:none';
  document.body.appendChild(probe);
  const height = probe.getBoundingClientRect().height;
  probe.remove();
  return height || window.innerHeight;
}

/** Three across, a frame this wide or wider carries the acts over its top
 *  edge, where nothing is drawn (the level cards start lowest, at 100 of
 *  the stage's 900): the stage keeps the height a row above it would take,
 *  about 9% of the frame on a 1280 × 720 laptop. Narrower — a phone on its
 *  side — and on a phone, where the camera frames close, they stand above.
 *  Checked by stepping the story and intersecting the row with everything
 *  drawn. */
const ACTS_OVER = 720;

/** How wide the frame can be with the whole stage on screen when the
 *  section is — as the page's autoplay and its snap leave it, top under the
 *  nav — once the words and the acts above it and the Play control below
 *  have their room. Measured: fixed reserves broke wherever the heading
 *  wrapped another way (an iPhone SE took three lines and lost the stage's
 *  foot under the Play control). Never so narrow its words stop reading: a
 *  screen too short for that scrolls instead. */
function useFitWidth(ref: React.RefObject<HTMLElement>, layout: Layout): number | undefined {
  const [width, setWidth] = useState<number>();
  useLayoutEffect(() => {
    const player = ref.current;
    const figure = player?.parentElement;
    const column = figure?.parentElement;
    const section = player?.closest('section');
    if (!player || !figure || !column || !section) return undefined;
    const aspect = layout === 'tall' ? 4 / 5 : 16 / 9;
    const least = layout === 'tall' ? 300 : 560;
    const fit = () => {
      const within = column.getBoundingClientRect();
      const stage = player.getBoundingClientRect();
      // Everything above the stage: the section's padding, the words, the acts.
      const above = (parseFloat(getComputedStyle(section).paddingTop) || 0) + (stage.top - within.top);
      const below = Math.max(0, within.bottom - stage.bottom);
      const control = document.querySelector('[data-autoplay-control]');
      const foot = control ? window.innerHeight - control.getBoundingClientRect().top + 12 : 24;
      const room = (steadyScreenHeight() - above - below - foot) * aspect;
      const most = column.clientWidth;
      setWidth(Math.round(clamp(room, Math.min(least, most), most)));
    };
    fit();
    const observer = new ResizeObserver(fit);
    observer.observe(column);
    window.addEventListener('resize', fit);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', fit);
    };
  }, [ref, layout]);
  return width;
}

/** Hands the story's light to the page's water, as the WEE story does:
 *  faded by how much of the section is on screen, so scrolling away brings
 *  the page's lights back up. It also tells the section (--how-night) so
 *  the heading and the acts turn to the light at night. */
function useHowLight(ref: React.RefObject<HTMLElement>, T: number, still: boolean) {
  const water = useWaterStory();
  const time = useRef(T);
  const write = useRef<() => void>(() => undefined);

  useEffect(() => {
    const player = ref.current;
    const section = player?.closest('section');
    const column = player?.parentElement?.parentElement;
    const story = water.current;
    if (!player || !section || !column) return undefined;
    write.current = () => {
      if (still) {
        story.how = NO_HOW_LIGHT;
        column.style.setProperty('--how-night', '0');
        story.repaint?.();
        return;
      }
      const view = window.innerHeight;
      const box = section.getBoundingClientRect();
      const shown = clamp((Math.min(box.bottom, view) - Math.max(box.top, 0)) / Math.max(1, Math.min(box.height, view)), 0, 1);
      const weight = ease.inOutCubic(clamp((shown - 0.35) / 0.4, 0, 1));
      const light = howLight(time.current);
      const own = player.getBoundingClientRect();
      story.how = {
        night: weight * light.night * NIGHT_DEPTH,
        dawn: weight * light.dawn,
        day: weight * light.day,
        focus: { x: own.left + own.width / 2, y: tween(light.rise, view * 1.08, own.top + own.height * 0.32, 0, 1, ease.linear) },
      };
      column.style.setProperty('--how-night', (weight * light.night).toFixed(3));
      story.repaint?.();
    };
    write.current();
    const onMove = () => write.current();
    window.addEventListener('scroll', onMove, { passive: true });
    window.addEventListener('resize', onMove);
    return () => {
      window.removeEventListener('scroll', onMove);
      window.removeEventListener('resize', onMove);
      story.how = NO_HOW_LIGHT;
      column.style.setProperty('--how-night', '0');
      story.repaint?.();
    };
  }, [ref, still, water]);

  useEffect(() => {
    time.current = T;
    write.current();
  }, [T]);
}

// ── The story ────────────────────────────────────────────────────────────────

const TELLING =
  'Maya asks The Home CEO to help her get the kids out of the house and gets the answer any AI gives today: try the library or a park. ' +
  'That night The Home CEO reviews her wish and builds what would have made it better: pre-fetched data, 100k+ family-friendly attractions and local events; ' +
  'domain expert models, evaluating physical play areas and inspirational memory development; ' +
  'and assistant capabilities, drive times and full-day schedules in the calendar. ' +
  'Next Saturday Jess makes the same wish and gets the new Ames NASA Visitor Center — Saturday at 10, a kids’ launch lab, a 22-minute drive — ' +
  'added to her calendar in one tap. The same wish at three levels: a generic answer, one great stop, a whole day planned.';

type WishStoryProps = {
  /** Called each time a telling finishes — the page's autoplay moves on then. */
  onCycleEnd?: () => void;
  /** Called when the reader moves the story with its acts: the telling runs on from there. */
  onSteer?: () => void;
};

export const WishStory: React.FC<WishStoryProps> = ({ onCycleEnd, onSteer }) => {
  const still = (useReducedMotion() ?? false) || FREEZE_FOR_CAPTURE;
  const playerRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<HTMLDivElement>(null);
  const layout: Layout = useMatches(WIDE_QUERY) ? 'wide' : 'tall';
  const fitWidth = useFitWidth(playerRef, layout);
  const { w, h } = useSize(frameRef);
  const [T, seek] = useStoryClock(playerRef, still, onCycleEnd);
  const actsOver = layout === 'wide' && (fitWidth ?? 0) >= ACTS_OVER;
  useHowLight(playerRef, T, still);

  let camera: Camera;
  if (layout === 'wide') camera = still ? FULL : wideCamera(T, focusZoom(w));
  else camera = onStage(still ? SHOTS.level(2) : tallCamera(T));
  const s = w / camera.wv;

  return (
    <Root>
      {!still && (
        <Acts
          T={T}
          onSeek={(t) => {
            seek(t);
            onSteer?.();
          }}
          width={fitWidth}
          over={actsOver}
        />
      )}
      <Player ref={playerRef} $layout={layout} style={{ width: fitWidth }}>
        <Frame ref={frameRef} aria-hidden="true">
          {w > 0 && (
            <Stage style={{ transform: `translate(${w / 2 - camera.cx * s}px, ${h / 2 - camera.cy * s}px) scale(${s})` }}>
              <Piece T={T} layout={layout} />
            </Stage>
          )}
        </Frame>
      </Player>
      <VisuallyHidden>{TELLING}</VisuallyHidden>
    </Root>
  );
};

const Root = styled.figure`
  position: relative;
  margin: 0;
`;

/* Phone-shaped, or three across; `useFitWidth` sets how wide. */
const Player = styled.div<{ $layout: Layout }>`
  position: relative;
  margin: 0 auto;

  ${({ $layout }) =>
    $layout === 'tall'
      ? css`
          aspect-ratio: 4 / 5;
          width: min(100%, 480px);
        `
      : css`
          aspect-ratio: 16 / 9;
          width: min(100%, 1120px);
        `}
`;

/* No box: the story stands on the page's water. Its edges fade, so the
   families walk in out of the page rather than past a frame's edge. */
const EDGE_X = 'linear-gradient(to right, transparent 0, #000 6%, #000 94%, transparent 100%)';
const EDGE_Y = 'linear-gradient(to bottom, transparent 0, #000 3%, #000 97%, transparent 100%)';
const Frame = styled.div`
  position: absolute;
  inset: 0;
  overflow: hidden;
  isolation: isolate;
  -webkit-mask-image: ${EDGE_X}, ${EDGE_Y};
  -webkit-mask-composite: source-in;
  mask-image: ${EDGE_X}, ${EDGE_Y};
  mask-composite: intersect;
`;

const Stage = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  width: ${W}px;
  height: ${H}px;
  transform-origin: 0 0;
`;

/* The acts, across the top of the stage and as wide: three slate
   rectangles with white names, the one playing filling with coral from its
   left as it plays. Sized to the row, not the screen: a phone on its side
   is wide, its stage small. */
const ActRow = styled.div<{ $over: boolean }>`
  container-type: inline-size;
  max-width: 100%;
  margin: 0 auto ${spacing.sm};

  ${({ $over }) =>
    $over &&
    css`
      position: absolute;
      top: ${spacing.sm};
      left: 50%;
      z-index: 1;
      margin: 0;
      transform: translateX(-50%);
    `}
`;

/* Thirds where the full names show. On a phone the middle fits its name
   and the outer two share the rest, so every name keeps one line in
   capitals: a second line would push the stage down onto the Play control
   on the smallest phones, where it's already as small as it goes. */
const ActGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 6px;

  @container (min-width: 460px) {
    grid-template-columns: repeat(3, 1fr);
    gap: ${spacing.sm};
  }
`;

/* The fill is the coral "next mom." wears, coral.dark (June, 2026-09-23:
   "use that coral for the background of the active section"; the deeper
   coral read too red). White can't be read on it (1.9:1), so where it has
   filled, the name turns to the rectangles' own slate (7.7:1); white on
   the slate reads 10:1 or better. */
const ACT_CORAL = colors.accent.coral.dark;
const ACT_INK_ON_CORAL = colors.highlight.extraDark;

/* The names in capital block letters, in Poppins (June, 2026-09-23), one
   weight for all three so the row never shifts as the acts change. */
const ActButton = styled.button`
  position: relative;
  overflow: hidden;
  min-width: 0;
  padding: 7px 6px;
  border: 1px solid ${colors.overlayWhite20};
  border-radius: ${borderRadius.md};
  background: linear-gradient(135deg, ${colors.highlight.dark}, ${colors.highlight.extraDark});
  box-shadow: 0 6px 16px ${colors.shadowSoft};
  color: ${colors.white};
  font-family: 'Poppins', ${typography.fontFamily.heading};
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  line-height: 1.3;
  text-align: center;
  cursor: pointer;
  transition: filter 0.2s ease;

  &:hover {
    filter: brightness(1.18);
  }

  &:focus-visible {
    outline: 2px solid ${byNight(colors.highlight.extraDark, colors.white)};
    outline-offset: 2px;
  }

  @container (min-width: 460px) {
    padding: 9px ${spacing.md};
    font-size: 13px;
    letter-spacing: 0.08em;
  }
`;

const ActFill = styled.span`
  position: absolute;
  inset: 0;
  background: ${ACT_CORAL};
  transform-origin: 0 50%;
`;

/* Laid over the button exactly as its own name is — same padding, centred
   the same way — so the two copies of the name coincide. */
const ActNameOnFill = styled.span`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: inherit;
  color: ${ACT_INK_ON_CORAL};
  pointer-events: none;
`;

/* A name that doesn't fit its rectangle takes two lines rather than an
   ellipsis. */
const ActName = styled.span`
  position: relative;
  display: block;
  text-wrap: balance;
`;

/* The full name where the row has room; June's short one where it doesn't. */
const Long = styled.span`
  display: none;

  @container (min-width: 460px) {
    display: inline;
  }
`;

const Short = styled.span`
  @container (min-width: 460px) {
    display: none;
  }
`;

const VisuallyHidden = styled.figcaption`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;
